import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from '@/components/ui/sonner'
import { ApiError, api, type CreateCaseRequest } from '@/services/api'
import { casesQueryKey } from './useCases'

/** Create-case mutation: invalidates the list, toasts, navigates to the new case. */
export function useCreateCase(opts?: { onSuccess?: () => void }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (input: CreateCaseRequest) => api.cases.create(input),
    onSuccess: (newCase, variables) => {
      void queryClient.invalidateQueries({ queryKey: casesQueryKey })
      const created = variables.initial_entities?.length ?? 0
      toast.success(t('cases.create.successTitle'), {
        description:
          created > 0
            ? t('cases.create.successDescWithEntities', { name: newCase.name, count: created })
            : t('cases.create.successDesc', { name: newCase.name }),
      })
      opts?.onSuccess?.()
      // Every case opens on its overview.
      navigate(`/cases/${newCase.id}`)
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : t('cases.create.failedTitle')
      toast.error(t('cases.create.failedTitle'), { description: msg })
    },
  })

  const error = mutation.error
    ? mutation.error instanceof ApiError
      ? mutation.error.message
      : t('cases.create.failedTitle')
    : null

  return { createCase: mutation.mutate, creating: mutation.isPending, error }
}
